import React, { useState, useEffect } from 'react';

function App() {
  const [terminalLines, setTerminalLines] = useState([]);
  const [currentCommand, setCurrentCommand] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showCursor, setShowCursor] = useState(true);

  const asciiArt = [
    "██╗  ██╗ █████╗ ███╗   ██╗ ██████╗ ███████╗██████╗ ",
    "██║  ██║██╔══██╗████╗  ██║██╔════╝ ██╔════╝██╔══██╗",
    "███████║███████║██╔██╗ ██║██║  ███╗█████╗  ██████╔╝",
    "██╔══██║██╔══██║██║╚██╗██║██║   ██║██╔══╝  ██╔══██╗",
    "██║  ██║██║  ██║██║ ╚████║╚██████╔╝███████╗██║  ██║",
    "╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═══╝ ╚═════╝ ╚══════╝╚═╝  ╚═╝",
    "                                              v1.0.0"
  ];

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(cursorInterval);
  }, []);

  useEffect(() => {
    const initTerminal = async () => {
      const initialLines = [
        { type: 'ascii', content: asciiArt },
        { type: 'output', content: '' },
        { type: 'output', content: "Type 'help' to see list of available commands." },
        { type: 'output', content: '' },
        { type: 'output', content: "The project is open-source 🎉 type 'repo' to check out the repository." },
        { type: 'output', content: '' },
        { type: 'output', content: "New 🎉: Try out the new 'ping' command. Check API health status." },
        { type: 'output', content: "New 🎉: New command 'about', for more information." },
        { type: 'output', content: '--' },
        { type: 'prompt', content: 'guest@maincode:~$ ' }
      ];
      setTerminalLines(initialLines);
    };
    initTerminal();
  }, []);

  const executeCommand = async (cmd) => {
    const newLines = [...terminalLines];
    newLines.push({ type: 'command', content: `guest@maincode:~$ ${cmd}` });

    switch (cmd.toLowerCase().trim()) {
      case 'help':
        newLines.push(
          { type: 'output', content: 'Available commands:' },
          { type: 'output', content: '  help     - Show this help message' },
          { type: 'output', content: '  clear    - Clear terminal' },
          { type: 'output', content: '  ping     - Check API health status' },
          { type: 'output', content: '  about    - About Hanger' },
          { type: 'output', content: '  repo     - View repository' },
          { type: 'output', content: '  banner   - Show ASCII banner' }
        );
        break;

      case 'clear':
        setTerminalLines([{ type: 'prompt', content: 'guest@maincode:~$ ' }]);
        return;

      case 'ping':
        newLines.push({ type: 'output', content: 'Checking API health...' });
        try {
          const response = await fetch('http://localhost:8000/ping');
          const data = await response.json();
          newLines.push(
            { type: 'output', content: `✓ API Status: ${data.status}` },
            { type: 'output', content: `✓ Message: ${data.message}` }
          );
        } catch (error) {
          newLines.push({ type: 'error', content: '✗ Failed to connect to API' });
        }
        break;

      case 'about':
        newLines.push(
          { type: 'output', content: 'Hanger - Modern Application Stack' },
          { type: 'output', content: '=================================' },
          { type: 'output', content: '• Frontend: React with Tailwind CSS' },
          { type: 'output', content: '• Backend: FastAPI with Mangum (Lambda-ready)' },
          { type: 'output', content: '• Local Dev: Docker Compose with hot reloading' },
          { type: 'output', content: '• Production: S3/CloudFront + AWS Lambda' }
        );
        break;

      case 'repo':
        newLines.push(
          { type: 'output', content: 'Repository: https://github.com/yourusername/hanger' },
          { type: 'output', content: 'Feel free to contribute!' }
        );
        break;

      case 'banner':
        newLines.push({ type: 'ascii', content: asciiArt });
        break;

      case '':
        break;

      default:
        newLines.push({ type: 'error', content: `Command not found: ${cmd}` });
        newLines.push({ type: 'output', content: "Type 'help' for available commands." });
    }

    newLines.push({ type: 'prompt', content: 'guest@maincode:~$ ' });
    setTerminalLines(newLines);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isTyping) {
      executeCommand(currentCommand);
      setCurrentCommand('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl">
        <div className="border-2 border-orange-500 rounded-lg shadow-2xl shadow-orange-500/20">
          <div className="bg-gray-800 px-4 py-2 flex items-center space-x-2 border-b border-orange-500">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-orange-500 text-sm font-mono ml-4">guest@maincode</span>
          </div>
          
          <div className="bg-gray-950 p-6 font-mono text-sm overflow-auto h-[600px]">
            {terminalLines.map((line, index) => (
              <div key={index} className="mb-1">
                {line.type === 'ascii' ? (
                  <pre className="text-yellow-400 text-xs sm:text-sm font-bold">
                    {line.content.join('\n')}
                  </pre>
                ) : line.type === 'prompt' ? (
                  <div className="flex">
                    <span className="text-green-500">guest@maincode</span>
                    <span className="text-white">:</span>
                    <span className="text-blue-500">~</span>
                    <span className="text-white">$ </span>
                    {index === terminalLines.length - 1 && (
                      <>
                        <input
                          type="text"
                          value={currentCommand}
                          onChange={(e) => setCurrentCommand(e.target.value)}
                          onKeyPress={handleKeyPress}
                          className="flex-1 bg-transparent text-white outline-none"
                          autoFocus
                        />
                        <span className={`text-white ${showCursor ? 'opacity-100' : 'opacity-0'}`}>
                          █
                        </span>
                      </>
                    )}
                  </div>
                ) : line.type === 'command' ? (
                  <div className="text-white">{line.content}</div>
                ) : line.type === 'error' ? (
                  <div className="text-red-500">{line.content}</div>
                ) : (
                  <div className="text-gray-300">{line.content}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;